/**
 * A concurrency limiter that limits the number of concurrent async operations.
 *
 * Features:
 * - Limits concurrent operations to a specified maximum
 * - Queues operations when the limit is reached
 * - Supports abort signals for cancellation
 * - Properly handles errors and cleanup
 */
export class ConcurrencyLimiter<T extends (...args: any[]) => Promise<any>> {
  private concurrentCount = 0;
  private queue: Array<{
    fn: () => Promise<any>;
    resolve: (value: any) => void;
    reject: (reason: any) => void;
    abortSignal?: AbortSignal;
  }> = [];

  constructor(private readonly maxConcurrent: number, private readonly wrappedFn: T) {}

  /**
   * Wraps the function call with concurrency limiting.
   */
  async execute(...args: Parameters<T>): Promise<ReturnType<T>> {
    // Extract abort signal if it's in the options
    const lastArg = args[args.length - 1];
    const abortSignal = lastArg?.signal instanceof AbortSignal ? lastArg.signal : undefined;

    // Check if already aborted
    abortSignal?.throwIfAborted();

    // If we're at the limit, queue this request
    if (this.concurrentCount >= this.maxConcurrent) {
      return new Promise<ReturnType<T>>((resolve, reject) => {
        const queueItem = {
          fn: () => this.wrappedFn(...args),
          resolve,
          reject,
          abortSignal,
        };

        // Listen for abort signal while in queue
        if (abortSignal) {
          const abortHandler = () => {
            // Remove from queue
            const index = this.queue.indexOf(queueItem);
            if (index !== -1) {
              this.queue.splice(index, 1);
              reject(new DOMException('The operation was aborted', 'AbortError'));
            }
          };
          abortSignal.addEventListener('abort', abortHandler, { once: true });
        }

        this.queue.push(queueItem);
      });
    }

    // We have capacity, execute immediately
    this.concurrentCount++;

    try {
      const result = await this.wrappedFn(...args);
      return result;
    } catch (error) {
      throw error;
    } finally {
      this.concurrentCount--;
      this.processQueue();
    }
  }

  /**
   * Process the next item in the queue.
   */
  private processQueue(): void {
    if (this.queue.length === 0 || this.concurrentCount >= this.maxConcurrent) {
      return;
    }

    const queueItem = this.queue.shift();
    if (!queueItem) return;

    // Check if aborted while in queue
    if (queueItem.abortSignal?.aborted) {
      queueItem.reject(new DOMException('The operation was aborted', 'AbortError'));
      this.processQueue(); // Try next item
      return;
    }

    this.concurrentCount++;

    queueItem
      .fn()
      .then(queueItem.resolve)
      .catch(queueItem.reject)
      .finally(() => {
        this.concurrentCount--;
        this.processQueue();
      });
  }

  getConcurrentCount(): number {
    return this.concurrentCount;
  }

  getQueueLength(): number {
    return this.queue.length;
  }
}

type Awaited<T> = T extends Promise<infer U> ? U : T;

export function createConcurrencyLimiter<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  maxConcurrent: number
): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
  const limiter = new ConcurrencyLimiter(maxConcurrent, fn);
  return (...args: Parameters<T>) => limiter.execute(...args) as Promise<Awaited<ReturnType<T>>>;
}
